// -----------------------------------------------------------------------------------------------------
// @ TOKEN SERVICE
//
// Methods are derivations of the Auth0 Angular-JWT helper service methods
// https://github.com/auth0/angular2-jwt
// -----------------------------------------------------------------------------------------------------

import { Observable, interval, map } from "rxjs";
import { TokenPayload } from "../auth/auth.type";
import { TokenStorageService } from "./token-storage.service";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { TokenExpirationDialogComponent } from "@app/shared/components/token-expiration-dialog/token-expiration-dialog.component";
import { MatDialog } from "@angular/material/dialog";

@Injectable()
export class TokenService {

    private tokenExpirationCheckInterval: number = 60000 // 1 minute
    private tokenRefreshBufferSeconds: number = 60 // Refresh token 60 seconds before it expires

    /**
     * Constructor
     */
    constructor(
        private _tokenStorageService: TokenStorageService,
        private _router: Router,
        private _dialog: MatDialog,
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Returns the decoded JWT as an object
     *
     * @param token
     */
    getDecodedToken(): TokenPayload | null {
        const accessToken = this._tokenStorageService.getAccessToken()
        if (!accessToken) {
            return null
        }
        return this._decodeToken(accessToken)
    }

    /**
     * 
     * @param tokenPayload 
     * @returns 
     */
    checkTokenExpirationInterval(): Observable<boolean | null> {
        return interval(this.tokenExpirationCheckInterval).pipe(
            map(() => this.checkTokenExpiration())
        );
    }

    /**
     * Check if token is expired or will expire soon (within buffer time)
     * @param tokenPayload 
     * @returns 
     */
    checkTokenExpiration(): boolean | null {
        const tokenPayload: TokenPayload | null = this.getDecodedToken()
        if (!tokenPayload) {
            return null
        }
        const expirationTime = tokenPayload.exp;
        const currentTime = Math.ceil(new Date().getTime() / 1000);

        // Check if token is expired OR will expire within the buffer time
        return currentTime > (expirationTime - this.tokenRefreshBufferSeconds);
    }

    /**
     * Check if token is actually expired (not considering buffer time)
     * @returns 
     */
    checkTokenActualExpiration(): boolean | null {
        const tokenPayload: TokenPayload | null = this.getDecodedToken()
        if (!tokenPayload) {
            return null
        }
        const expirationTime = tokenPayload.exp;
        const currentTime = Math.ceil(new Date().getTime() / 1000);

        return currentTime > expirationTime;
    }

    /**
     * 
     * @returns 
     */
    checkRefreshTokenExpirationInterval(): Observable<boolean> {
        return interval(this.tokenExpirationCheckInterval).pipe(
            map(() => this.checkRefreshTokenExpiration())
        );
    }

    /**
     * 
     * @returns 
     */
    checkRefreshTokenExpiration(): boolean {
        const refreshTokenExpiry = this._tokenStorageService.getRefreshToken()
        if (refreshTokenExpiry === null) {
            return true
        }
        return false
    }

    /**
     * remove open token expiration dialog
     */
    removeTokensAndOpenSessionDialog(type: string) {

        // ignore if in login
        if (this._router.url === '/login') {
            return
        }

        if (type === undefined) {
            type = 'session_expired';
        }

        for (let dialogRef of this._dialog.openDialogs) {
            if (dialogRef.componentInstance instanceof TokenExpirationDialogComponent) {
                return
            }
        }

        const dialogRef = this._dialog.open<TokenExpirationDialogComponent>(
            TokenExpirationDialogComponent, {
            data: { 'source': type },
            minWidth: 500,
            panelClass: 'custom-mat-dialog-container'
        }
        )

        dialogRef.afterClosed().subscribe((response) => {
            if (response.event == 'logout') {
                this.logout();
            }
        })
    }

    /**
     * 
     */
    logout() {
        this._tokenStorageService.removeTokens();
        this._router.navigate(['login']);
        window.location.reload();
    }

    /**
     * 
     * @param accessToken 
     */
    saveAccessToken(accessToken: string) {
        this._tokenStorageService.saveAccessToken(accessToken)
    }

    /**
     * 
     * @param refreshToken 
     */
    saveRefreshToken(refreshToken: string, expiry: number) {
        this._tokenStorageService.saveRefreshToken(refreshToken, expiry)
    }

    /**
     * Get the token refresh buffer time in seconds
     * @returns number of seconds before expiration when refresh should be triggered
     */
    getTokenRefreshBufferSeconds(): number {
        return this.tokenRefreshBufferSeconds;
    }

    /**
     * Set the token refresh buffer time in seconds
     * @param seconds number of seconds before expiration when refresh should be triggered
     */
    setTokenRefreshBufferSeconds(seconds: number) {
        this.tokenRefreshBufferSeconds = seconds;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Base64 decoder
     * Credits: https://github.com/atk
     *
     * @param str
     * @private
     */
    private _b64decode(str: string): string {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/='
        let output = ''

        str = String(str).replace(/=+$/, '')

        if (str.length % 4 === 1) {
            throw new Error("'atob' failed: The string to be decoded is not correctly encoded.")
        }

        /* eslint-disable */
        for (
            // initialize result and counters
            let bc = 0, bs: any, buffer: any, idx = 0;
            // get next character
            (buffer = str.charAt(idx++));
            // character found in table? initialize bit storage and add its ascii value;
            ~buffer &&
                ((bs = bc % 4 ? bs * 64 + buffer : buffer),
                    // and if not first of each 4 characters,
                    // convert the first 8 bits to one ascii character
                    bc++ % 4)
                ? (output += String.fromCharCode(255 & (bs >> ((-2 * bc) & 6))))
                : 0
        ) {
            // try to find character in table (0-63, not found => -1)
            buffer = chars.indexOf(buffer)
        }
        /* eslint-enable */

        return output
    }

    /**
     * Base64 unicode decoder
     *
     * @param str
     * @private
     */
    private _b64DecodeUnicode(str: any): string {
        return decodeURIComponent(
            Array.prototype.map.call(this._b64decode(str), (c: any) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''),
        )
    }

    /**
     * URL Base 64 decoder
     *
     * @param str
     * @private
     */
    private _urlBase64Decode(str: string): string {
        let output = str.replace(/-/g, '+').replace(/_/g, '/')
        switch (output.length % 4) {
            case 0: {
                break
            }
            case 2: {
                output += '=='
                break
            }
            case 3: {
                output += '='
                break
            }
            default: {
                throw Error('Illegal base64url string!')
            }
        }
        return this._b64DecodeUnicode(output)
    }

    /**
     * Decode token
     *
     * @param token
     * @private
     */
    private _decodeToken(token: string): TokenPayload {

        // Split the token
        const parts = token.split('.')

        if (parts.length !== 3) {
            throw new Error(
                "The inspected token doesn't appear to be a JWT. Check to make sure it has three parts and see https://jwt.io for more.",
            )
        }

        // Decode the token using the Base64 decoder
        const decoded = this._urlBase64Decode(parts[1])

        if (!decoded) {
            throw new Error('Cannot decode the token.')
        }

        const jsonTokenPayload = JSON.parse(decoded)

        const tokenPayload: TokenPayload = {
            sub: jsonTokenPayload['sub'],
            assessor: jsonTokenPayload['assessor'],
            scopes: jsonTokenPayload['scopes'],
            iat: jsonTokenPayload['iat'],
            exp: jsonTokenPayload['exp'],
        }

        return tokenPayload;
    }

}